import { Box, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { ReactNode, useState } from "react";

import { Barras, Cabecalho, Cartao, ErroAba, Kpi, nomeProduto, num, pctInt, reais } from "./comum";
import { IRhAbas, SeloVisibilidadeAba } from "./types";
import { SeloVisibilidade } from "./Visibilidade";

const COR_GRUPO: Record<string, string> = {
  "A caminho": "#86B6EF",
  "Pedem olhar": "#EB6834",
  "Fora por regra ou escolha": "#8E8BD6",
  "Sem estado apurado": "#B5B4AE",
};

function Celula({ fundo, cor, selo, valor, texto }: { fundo: string; cor: string; selo: string; valor: number; texto: ReactNode }) {
  return (
    <Box sx={{ bgcolor: fundo, borderRadius: 2, p: { xs: 1, sm: 2 }, display: "flex", flexDirection: "column", gap: 0.5, minHeight: 112, minWidth: 0 }}>
      <Typography variant="caption" fontWeight={700} sx={{ color: cor }}>
        {selo}
      </Typography>
      <Typography sx={{ fontFamily: "Sora", fontSize: { xs: "1.4rem", sm: "2rem" }, fontWeight: 700, lineHeight: 1.1 }}>{num(valor)}</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
        {texto}
      </Typography>
    </Box>
  );
}

/** Aba "Ativos e funcionários" (escopo 2.3 §2, catálogo M1 a M6). */
export function AtivosTab({ dados, erro, selo }: { dados?: IRhAbas; erro: boolean; selo?: SeloVisibilidadeAba | null }) {
  const [escolhido, setEscolhido] = useState<string | null>(null);
  if (erro || !dados?.ativos) return <ErroAba nome="Ativos e funcionários" />;

  const { titularesNaFolha, produtos } = dados.ativos;
  const p = produtos.find((x) => x.produto === escolhido) ?? produtos[0];
  if (!p) return <ErroAba nome="Ativos e funcionários" />;
  const nome = nomeProduto(p.produto).toLowerCase();
  const aCaminho = p.porqueSemPlano.find((g) => g.rotulo === "A caminho")?.n ?? 0;
  const inv = p.investimento;

  return (
    <Box display="flex" flexDirection="column" gap={3} maxWidth={1200}>
      <Cabecalho
        sobretitulo="Ativos e funcionários"
        titulo="Quem trabalha aqui, o que tem e quanto você investe"
        dados={dados}
        unidade="cada pessoa contada uma vez"
        aoLadoDoTitulo={<SeloVisibilidade selo={selo} />}
      />

      {produtos.length > 1 && (
        <ToggleButtonGroup
          exclusive
          size="small"
          value={p.produto}
          onChange={(_, v) => v && setEscolhido(v)}
          aria-label="Produto"
        >
          {produtos.map((x) => (
            <ToggleButton key={x.produto} value={x.produto} sx={{ textTransform: "none", px: 2 }}>
              {nomeProduto(x.produto)}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      )}

      <Box display="grid" gap={2} gridTemplateColumns="repeat(auto-fit, minmax(200px, 1fr))">
        <Kpi rotulo="Titulares na folha" valor={num(titularesNaFolha)} nota="do eSocial, uma vez cada" />
        <Kpi
          rotulo={`Têm ${nome}`}
          valor={p.cobertura === null ? "-" : pctInt(p.cobertura)}
          nota={`${num(p.matriz.folhaComPlano)} titulares`}
        />
        <Kpi
          rotulo={`Na folha, sem ${nome} confirmado`}
          valor={num(p.matriz.folhaSemPlano)}
          nota={`${num(aCaminho)} já estão a caminho`}
          cor="#B84F0E"
        />
        <Kpi
          rotulo={`Com ${nome}, fora da folha, sem saída a caminho`}
          valor={num(p.matriz.zonaCinza)}
          nota="permanência legal ou saída que não chegou à operadora"
          cor="#B42318"
        />
        <Kpi
          rotulo="Investimento por titular"
          valor={inv ? reais(inv.media) : null}
          nota={inv ? "por mês, com a família · estimado" : "falta o valor do plano nos contratos"}
          cor="#3024E1"
        />
      </Box>

      {produtos.length > 1 && (
        <Cartao titulo="O que a sua gente tem" subtitulo="Cada produto contra a folha inteira. As linhas não somam: uma pessoa pode ter mais de um.">
          <Barras
            itens={produtos.map((x) => ({
              rotulo: nomeProduto(x.produto),
              n: x.matriz.folhaComPlano,
              nota: x.cobertura === null ? "-" : `${pctInt(x.cobertura)} · ${num(x.matriz.folhaComPlano)}`,
            }))}
          />
        </Cartao>
      )}

      <Cartao
        titulo={`Folha x ${nome}`}
        subtitulo="A folha vem do eSocial; o plano, da operadora e da plataforma. Onde os dois discordam, há o que fazer."
      >
        <Box display="grid" gridTemplateColumns={{ xs: "64px repeat(2, minmax(0, 1fr))", sm: "110px repeat(2, minmax(0, 1fr))" }} gap={1}>
          <span />
          <Typography variant="caption" fontWeight={700} color="text.secondary">
            Com {nome}
          </Typography>
          <Typography variant="caption" fontWeight={700} color="text.secondary">
            Sem {nome} confirmado
          </Typography>
          <Typography variant="body2" fontWeight={700} alignSelf="center">
            Na folha
          </Typography>
          <Celula fundo="#EEF0FF" cor="#1F7A44" selo="Como deve ser" valor={p.matriz.folhaComPlano} texto={`trabalham aqui e têm ${nome}`} />
          <Celula fundo="#FFF3EB" cor="#B84F0E" selo="Veja o porquê abaixo" valor={p.matriz.folhaSemPlano} texto={`trabalham aqui e ainda não têm ${nome} confirmado`} />
          <Typography variant="body2" fontWeight={700} alignSelf="center">
            Fora da folha
          </Typography>
          <Celula
            fundo="#FDECEC"
            cor="#B42318"
            selo="Ainda no plano"
            valor={p.matriz.foraComPlano.n}
            texto={p.matriz.foraComPlano.itens.map((i) => `${i.rotulo}: ${num(i.n)}`).join(" · ") || "ninguém"}
          />
          <Celula
            fundo="#F5F5F7"
            cor="#5B6170"
            selo="Saída concluída"
            valor={p.matriz.saidaConcluida.n}
            texto={`saíram da folha e do plano; ${num(p.matriz.saidaConcluida.noMes)} neste mês`}
          />
        </Box>
      </Cartao>

      <Box display="grid" gap={2} gridTemplateColumns="repeat(auto-fit, minmax(340px, 1fr))">
        <Cartao titulo={`Por que ${num(p.matriz.folhaSemPlano)} estão sem ${nome} confirmado`} subtitulo="Uma razão por pessoa.">
          {p.porqueSemPlano.length > 0 && (
            <Box display="flex" height={24} gap="2px" borderRadius={1} overflow="hidden" role="img" aria-label={p.porqueSemPlano.map((g) => `${g.rotulo}: ${g.n}`).join(", ")}>
              {p.porqueSemPlano.map((g) => (
                <Box key={g.rotulo} sx={{ flex: `${g.n} 1 0`, minWidth: 3, bgcolor: COR_GRUPO[g.rotulo] ?? "grey.400" }} />
              ))}
            </Box>
          )}
          {p.porqueSemPlano.map((g) => (
            <Box key={g.rotulo} display="flex" flexDirection="column" gap={0.5}>
              <Box display="flex" alignItems="center" gap={1}>
                <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: COR_GRUPO[g.rotulo] ?? "grey.400" }} />
                <Typography variant="body2" fontWeight={700}>
                  {g.rotulo}
                </Typography>
                <Typography variant="body2" fontWeight={700} ml="auto">
                  {num(g.n)}
                </Typography>
              </Box>
              {g.itens.map((i) => (
                <Box key={i.rotulo} display="flex" pl={2.5}>
                  <Typography variant="body2" color="text.secondary">
                    {i.rotulo}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" ml="auto">
                    {num(i.n)}
                  </Typography>
                </Box>
              ))}
            </Box>
          ))}
        </Cartao>

        <Cartao titulo="Quanto você investe por titular" subtitulo={`${nomeProduto(p.produto)} da família, por mês. Estimado pelo valor médio de cada contrato.`}>
          {inv ? (
            <>
              <Box display="flex" alignItems="baseline" gap={1.5} flexWrap="wrap">
                <Typography sx={{ fontFamily: "Sora", fontSize: "2.25rem", fontWeight: 700 }}>{reais(inv.media)}</Typography>
                <Typography variant="body2" color="text.secondary">
                  média · mediana {reais(inv.mediana)} · {reais(inv.totalMes)} por mês no total
                </Typography>
              </Box>
              <Barras itens={inv.faixas} />
              {inv.titularesComValor < inv.titularesComPlano && (
                <Typography variant="caption" color="text.secondary">
                  {num(inv.titularesComValor)} de {num(inv.titularesComPlano)} titulares têm valor de plano apurado.
                </Typography>
              )}
            </>
          ) : (
            <Typography color="text.secondary">Ainda não apurado: falta o valor do plano na maioria dos contratos.</Typography>
          )}
        </Cartao>
      </Box>

      <Box display="grid" gap={2} gridTemplateColumns="repeat(auto-fit, minmax(340px, 1fr))">
        <Cartao titulo={`Onde estão os ${num(p.matriz.folhaComPlano)} com ${nome}`} subtitulo="Pela operadora do contrato em que cada um tem o plano">
          <Barras itens={p.operadoras} cinza="Sem operadora informada" />
        </Cartao>
        <Cartao titulo="Dependentes">
          <Typography sx={{ fontFamily: "Sora", fontSize: "2.25rem", fontWeight: 700 }}>{num(p.dependentesComPlano)}</Typography>
          <Typography variant="body2" color="text.secondary">
            dependentes com {nome}, dos titulares da folha
          </Typography>
        </Cartao>
      </Box>
    </Box>
  );
}
